import { useMemo, useState, useRef, useId, type FormEvent } from "react";
import { Link } from "./Navigation";
import {
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Plus,
  PhoneMissed,
  TrendingUp,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  calculateProfitRecovery,
  AI_RECOVERY_RATE,
} from "@/lib/profitCalculator";
import {
  createWebsiteLeadSubmitter,
  type LeadSubmissionResult,
} from "@/lib/leadSubmission";
import { money } from "./commerce";
import { LeadReceipt } from "./Contact";
import "./calculator.css";

export function Calculator() {
  const instance = useId();
  const submitLead = useRef(createWebsiteLeadSubmitter());
  const [calls, setCalls] = useState(200),
    [jobValue, setJobValue] = useState(400),
    [missedRate, setMissedRate] = useState(20),
    [closeRate, setCloseRate] = useState(25);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [result, setResult] = useState<LeadSubmissionResult | null>(null);
  const calc = useMemo(
    () => calculateProfitRecovery({ calls, jobValue, missedRate, closeRate }),
    [calls, jobValue, missedRate, closeRate],
  );
  const request = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const d = new FormData(e.currentTarget);
    setBusy(true);
    setError("");
    try {
      setResult(
        await submitLead.current({
          kind: "calculator",
          email: String(d.get("email")),
          calls,
          jobValue,
          missedRate,
          closeRate,
          annualRecovery: calc.annualSaved,
          honeypot: String(d.get("website") || ""),
        }),
      );
    } catch {
      setError(
        "We couldn’t confirm your request. Please retry or contact the team.",
      );
    } finally {
      setBusy(false);
    }
  };
  const inputs = [
    {
      name: "Monthly inbound calls",
      hint: "How often does your phone ring?",
      value: calls,
      set: setCalls,
      min: 10,
      max: 3000,
      step: 10,
      format: (v: number) => v.toLocaleString(),
    },
    {
      name: "Average job value",
      hint: "The value of one booked job.",
      value: jobValue,
      set: setJobValue,
      min: 50,
      max: 5000,
      step: 50,
      format: money,
    },
    {
      name: "Calls currently missed",
      hint: "During jobs, after hours, or on another call.",
      value: missedRate,
      set: setMissedRate,
      min: 5,
      max: 80,
      step: 1,
      format: (v: number) => `${v}%`,
    },
    {
      name: "Close rate on answered calls",
      hint: "How many of those calls turn into work?",
      value: closeRate,
      set: setCloseRate,
      min: 5,
      max: 60,
      step: 1,
      format: (v: number) => `${v}%`,
    },
  ];
  return (
    <section
      className="rc-calculator g-tone g-tone--cool"
      id="profit-engine"
      aria-labelledby={`${instance}-title`}
    >
      <div className="rb-wrap">
        <header className="rc-heading">
          <div>
            {/* The home page's third and last eyebrow (the brief caps it at
                three): the deck opens on one, the plans open on one, and the
                bottom third of the page had none. Same words, moved from the
                quiet .g-label tier up to the chapter tier. */}
            <p className="rc-kicker g-eyebrow">Revenue recovery calculator</p>
            <h2 id={`${instance}-title`}>
              Your next opportunity might already be calling.
            </h2>
          </div>
          <p>
            What happens when more of those calls get an answer? Put your numbers
            to work.
          </p>
        </header>
        <div className="rc-workbench g-panel g-panel--strong g-panel--flush">
          <div className="rc-inputs">
            <div className="rc-panel-heading">
              <span>Your business today</span>
              <span>Adjust to explore</span>
            </div>
            {inputs.map((input, i) => (
              <div className="rc-control" key={input.name}>
                <div className="rc-control-top">
                  <div>
                    <label id={`${instance}-input-${i}`}>{input.name}</label>
                    <p>{input.hint}</p>
                  </div>
                  <output aria-live="off">{input.format(input.value)}</output>
                </div>
                <div className="rc-range">
                  <button
                    type="button"
                    className="g-arrow"
                    aria-label={`Decrease ${input.name.toLowerCase()}`}
                    disabled={input.value <= input.min}
                    onClick={() =>
                      input.set(Math.max(input.min, input.value - input.step))
                    }
                  >
                    <Minus size={16} />
                  </button>
                  <Slider
                    className="rc-slider"
                    aria-labelledby={`${instance}-input-${i}`}
                    aria-valuetext={input.format(input.value)}
                    value={[input.value]}
                    onValueChange={(v) => input.set(v[0])}
                    min={input.min}
                    max={input.max}
                    step={input.step}
                  />
                  <button
                    type="button"
                    className="g-arrow"
                    aria-label={`Increase ${input.name.toLowerCase()}`}
                    disabled={input.value >= input.max}
                    onClick={() =>
                      input.set(Math.min(input.max, input.value + input.step))
                    }
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}
            <div className="rc-missed">
              <PhoneMissed size={22} />
              <p>
                <strong>{calc.missedCalls} calls</strong> missed each month at
                these settings.
              </p>
              <ArrowDownRight size={22} />
            </div>
          </div>
          <div className="rc-outcome">
            <div className="rc-panel-heading">
              <span>The opportunity</span>
              <TrendingUp size={20} />
            </div>
            <p className="rc-annual-label">Estimated annual revenue recovered</p>
            {/* Keyed on the value, so React replaces the element whenever the
                figure changes and the settle animation in calculator.css runs
                again. Nothing else re-renders, nothing moves, and with
                prefers-reduced-motion the animation is not declared at all. */}
            <strong className="rc-annual" key={calc.annualSaved}>
              {money(calc.annualSaved)}
            </strong>
            <p className="rc-monthly">
              <strong>{money(calc.recovered)}</strong> per month before
              subscription and usage costs
            </p>
            <div className="rc-bars" aria-label="Monthly opportunity breakdown">
              <div>
                <span>Revenue lost to missed calls</span>
                <b className="rc-figure-lost">{money(calc.revenueLost)}</b>
              </div>
              <div className="rc-bar rc-bar-lost">
                <span />
              </div>
              <div>
                <span>Potentially recovered</span>
                <b className="rc-figure-recovered">{money(calc.recovered)}</b>
              </div>
              <div className="rc-bar rc-bar-recovered">
                <span style={{ transform: `scaleX(${AI_RECOVERY_RATE})` }} />
              </div>
            </div>
            <p className="rb-fine">
              Illustrative estimate, not a forecast or guarantee. It assumes seven
              in ten of the currently missed opportunities come back, using the
              job value and close rate you set. Real results vary.
            </p>
            <details className="rc-math">
              <summary>See the calculation</summary>
              <p>
                {calls} calls at the missed rate you set ≈ {calc.missedCalls}{" "}
                missed calls. Multiply by your close rate, {money(jobValue)} job
                value, and seven in ten recovered. Rounded monthly recovery over
                twelve months = {money(calc.annualSaved)}.
              </p>
            </details>
            {result ? (
              <LeadReceipt result={result} />
            ) : (
              <form
                onSubmit={request}
                className="rb-form rc-report"
                action="https://formsubmit.co/chleb@chleb.ai"
                method="post"
              >
                <label>
                  Email me a breakdown
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={255}
                    placeholder="you@yourbusiness.com"
                    autoComplete="email"
                  />
                </label>
                <label className="rb-honeypot" aria-hidden="true">
                  Leave empty
                  <input name="website" tabIndex={-1} />
                </label>
                <input
                  type="hidden"
                  name="estimatedAnnualRecovery"
                  value={calc.annualSaved}
                />
                {error && (
                  <p role="alert" className="rb-form-error">
                    {error}
                  </p>
                )}
                <button className="rb-button" type="submit" disabled={busy}>
                  {busy ? "Sending…" : "Request breakdown"}
                  <ArrowUpRight size={17} />
                </button>
                <p className="rb-fine">
                  The team will review your request.{" "}
                  <Link to="/privacy-policy">Privacy policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
